import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useCatalogs, useContactOptions, useUsers, catalogMap } from "@/lib/lookups";
import { TagsInput } from "@/components/common/TagsInput";

const empty = (f) => (f.type === "tags" || f.type === "contacts" ? [] : f.type === "bool" ? false : "");
const toForm = (fields, item) => Object.fromEntries(fields.map((f) => [f.key, item?.[f.key] ?? empty(f)]));
const toValue = (f, raw) => {
  if (f.type === "bool") return !!raw;
  if (f.type === "tags" || f.type === "contacts") return raw || [];
  if (raw === "" || raw == null) return null;
  if (f.type === "money" || f.type === "pct" || f.type === "int") return Number(raw);
  return typeof raw === "string" ? raw.trim() || null : raw;
};
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const Select = ({ common, options }) => (
  <select {...common}>
    <option value="">Sin definir</option>
    {Object.entries(options).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
  </select>
);

const ContactsPicker = ({ value, onChange, contacts, testid }) => (
  <div data-testid={testid} className="c-input !p-2 max-h-40 overflow-y-auto space-y-1">
    {contacts.length === 0 && <p className="text-xs c-muted px-1">Aún no hay contactos.</p>}
    {contacts.map((c) => (
      <label key={c.id} className="flex items-center gap-2 text-sm px-1">
        <input type="checkbox" checked={value.includes(c.id)} onChange={(e) => onChange(e.target.checked ? [...value, c.id] : value.filter((x) => x !== c.id))} />
        {c.name}{c.company ? <span className="c-muted text-xs">· {c.company}</span> : null}
      </label>
    ))}
  </div>
);

export const Input = ({ field, value, onChange, lookups }) => {
  const testid = `form-${field.key.replace(/_/g, "-")}`;
  const common = { id: `f-${field.key}`, "data-testid": testid, className: "c-input", value: value ?? "", onChange: (e) => onChange(e.target.value) };
  const { cats, contacts, users } = lookups;
  if (field.type === "select") return <Select common={common} options={field.options || catalogMap(cats, field.catalog)} />;
  if (field.type === "contact") return <Select common={common} options={Object.fromEntries(contacts.map((c) => [c.id, c.name + (c.company ? ` · ${c.company}` : "")]))} />;
  if (field.type === "user") return <Select common={common} options={Object.fromEntries(users.map((u) => [u.id, u.name]))} />;
  if (field.type === "contacts") return <ContactsPicker value={value || []} onChange={onChange} contacts={contacts} testid={testid} />;
  if (field.type === "tags") return <TagsInput value={value || []} onChange={onChange} testid={testid} suggestions={(cats[field.catalog] || []).map((i) => i.label)} />;
  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 c-input cursor-pointer">
        <input type="checkbox" data-testid={testid} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="w-5 h-5 accent-[#bc9538]" />
        <span className="text-sm">{field.hint || "Sí"}</span>
      </label>
    );
  }
  if (field.type === "textarea") return <textarea {...common} rows={3} />;
  const type = field.type === "date" ? "date" : ["text", "tel", "email"].includes(field.type) ? field.type : "number";
  return <input {...common} type={type} inputMode={type === "number" ? "decimal" : undefined} step="any" required={field.required} />;
};

export const FieldsForm = ({ open, onOpenChange, title, fields, item, onSubmit, testid, description }) => {
  const { isOwner } = useAuth();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const visible = fields.filter((f) => !f.ownerOnly || isOwner);
  const needsContacts = open && visible.some((f) => f.type === "contact" || f.type === "contacts");
  const lookups = { cats: useCatalogs(), contacts: useContactOptions(needsContacts), users: useUsers(open && visible.some((f) => f.type === "user")) };
  useEffect(() => { if (open) setForm(toForm(fields, item)); }, [open, fields, item]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {};
    visible.forEach((f) => {
      const v = toValue(f, form[f.key]);
      if (!item ? !(v === null || (Array.isArray(v) && !v.length) || v === false) : !same(v, item[f.key] ?? (f.type === "tags" || f.type === "contacts" ? [] : f.type === "bool" ? false : null))) payload[f.key] = v;
    });
    setBusy(true);
    try { await onSubmit(payload); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid={testid} className="max-h-[92vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading">{title}</SheetTitle>
          <SheetDescription>{description || 'Deja vacío lo que no sepas: se mostrará como "Sin definir".'}</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-4 space-y-3 max-w-md mx-auto">
          {visible.map((f) => (
            <div key={f.key} className="space-y-1">
              <label htmlFor={`f-${f.key}`} className="text-xs font-medium c-text-2">{f.label}{f.required ? " *" : ""}</label>
              <Input field={f} value={form[f.key]} lookups={lookups} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} />
            </div>
          ))}
          <button data-testid="form-submit-btn" disabled={busy} className="c-btn-primary w-full py-3.5 mt-2 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
